import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  MapPin, DollarSign, TrendingUp, CheckCircle, AlertTriangle,
  Target, Building2, Phone, Mail, Star, ChevronRight, FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(n) {
  if (!n && n !== 0) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`p-4 rounded-lg border ${accent ? "bg-[#D4AF37]/[0.06] border-[#D4AF37]/20" : "bg-white/[0.02] border-white/[0.06]"}`}>
      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${accent ? "text-[#D4AF37]" : "text-white"}`}>{value}</p>
    </div>
  );
}

function GradeTag({ grade }) {
  const cfg = {
    "A+": "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "A":  "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "B+": "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "B":  "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "C":  "bg-[#FF9F0A]/10 border-[#FF9F0A]/30 text-[#FF9F0A]",
  };
  return (
    <span className={`px-3 py-1 border rounded-full font-bold text-sm ${cfg[grade] || cfg["B"]}`}>
      {grade}
    </span>
  );
}

export default function PublicDealPackagePage() {
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/deals/package/${token}`);
        setPkg(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Package not found or expired." : "Failed to load package.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin mx-auto" />
          <p className="text-white/40 text-sm">Loading deal package…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-12 h-12 text-[#FF9F0A] mx-auto" />
          <p className="text-white font-bold text-lg">{error}</p>
          <a href="/" className="text-[#D4AF37] text-sm hover:underline">← Back to DealForge</a>
        </div>
      </div>
    );
  }

  const memo = pkg.memo || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-[#050505]/80 backdrop-blur-sm sticky top-0 z-20 px-6 py-4 flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <span className="text-[#D4AF37] font-bold tracking-[2px] text-sm uppercase">DEAL<span className="text-[#C4A030]">FORGE</span></span>
          <span className="text-white/20">|</span>
          <span className="text-white/40 text-sm">Deal Package</span>
        </div>
        <div className="flex items-center gap-2 text-white/30 text-xs">
          <span>{pkg.view_count || 1} view{(pkg.view_count || 1) !== 1 ? "s" : ""}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Hero Card */}
        <div className="bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-white/[0.08] rounded-xl p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded-full uppercase tracking-wider font-semibold">
                  Investment Opportunity
                </span>
                {memo.deal_grade && <GradeTag grade={memo.deal_grade} />}
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">{memo.deal_title || pkg.property_address}</h1>
              {memo.tagline && <p className="text-white/50">{memo.tagline}</p>}
            </div>
            {memo.confidence_score && (
              <div className="text-center flex-shrink-0">
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Confidence</p>
                <p className="text-3xl font-bold text-[#D4AF37]">{memo.confidence_score}</p>
                <p className="text-white/30 text-[10px]">/ 100</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-white/40 text-sm mb-4">
            <MapPin className="w-4 h-4 text-[#D4AF37]" />
            <span>{pkg.property_address}</span>
            {pkg.property_type && <span className="px-2 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded text-xs">{pkg.property_type}</span>}
            {pkg.beds && <span className="text-white/30">{pkg.beds}bd/{pkg.baths}ba</span>}
            {pkg.sqft && <span className="text-white/30">{Number(pkg.sqft).toLocaleString()} sqft</span>}
          </div>

          {memo.executive_summary && (
            <p className="text-white/60 text-sm leading-relaxed border-t border-white/[0.06] pt-4">
              {memo.executive_summary}
            </p>
          )}
        </div>

        {/* Financials */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Purchase Price" value={fmt(pkg.purchase_price)} />
          <StatBox label="Rehab Estimate" value={fmt(pkg.rehab_estimate)} />
          <StatBox label="After Repair Value" value={fmt(pkg.arv)} />
          <StatBox label="Projected Profit" value={fmt(pkg.spread)} accent />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <StatBox label="ROI" value={`${pkg.roi_pct}%`} accent />
          <StatBox label="Capital Needed" value={fmt(pkg.capital_needed)} />
        </div>

        {/* Investment Highlights */}
        {memo.investment_highlights?.length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] text-[#00FF88]/60 uppercase tracking-wider font-semibold mb-3 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-[#00FF88]" /> Investment Highlights
            </p>
            <ul className="space-y-2">
              {memo.investment_highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00FF88] mt-0.5 flex-shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Exit Strategy + Risk */}
        <div className="grid grid-cols-2 gap-4">
          {(memo.exit_strategy || pkg.exit_strategy) && (
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <p className="text-[10px] text-[#D4AF37]/60 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-[#D4AF37]" /> Exit Strategy
              </p>
              <p className="text-white/70 text-sm">{typeof memo.exit_strategy === "string" ? memo.exit_strategy : pkg.exit_strategy}</p>
            </div>
          )}
          {memo.risk_summary && (
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <p className="text-[10px] text-[#FF9F0A]/60 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-[#FF9F0A]" /> Risk Summary
              </p>
              <p className="text-white/60 text-sm">{memo.risk_summary}</p>
            </div>
          )}
        </div>

        {/* Contact / CTA */}
        {pkg.creator_name && (
          <div className="bg-gradient-to-br from-[#D4AF37]/[0.08] to-[#D4AF37]/[0.03] border border-[#D4AF37]/20 rounded-xl p-6">
            <p className="text-[10px] text-[#D4AF37]/60 uppercase tracking-wider font-semibold mb-3">Presented By</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-lg">{pkg.creator_name}</p>
                {pkg.creator_email && (
                  <a href={`mailto:${pkg.creator_email}`} className="flex items-center gap-1.5 text-white/50 text-sm hover:text-[#D4AF37] transition-colors mt-1">
                    <Mail className="w-4 h-4" /> {pkg.creator_email}
                  </a>
                )}
              </div>
              {pkg.creator_email && (
                <a href={`mailto:${pkg.creator_email}?subject=Interested in ${pkg.property_address}&body=Hi ${pkg.creator_name}, I reviewed your deal package and I'm interested in learning more.`}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold rounded-lg text-sm transition-all">
                  <Mail className="w-4 h-4" /> Contact Investor
                </a>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 border-t border-white/[0.04] space-y-2">
          <p className="text-white/20 text-xs">
            Shared via <span className="text-[#D4AF37] font-bold tracking-wider">DEALFORGE™</span> · For informational purposes only. Not financial or investment advice.
          </p>
          <a href="/" className="text-white/20 hover:text-white/40 text-xs transition-colors">
            Create your own deal package →
          </a>
        </div>
      </div>
    </div>
  );
}
